import { Box, Button, Stack, Typography, IconButton, Tooltip, Divider } from '@mui/material'
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material'
import { DataTable } from '../../components/ui/DataTable'
import { SearchBar } from '../../components/ui/SearchBar'
import { LoadingSpinner } from '../../components/shared/LoadingSpinner'
import { themeColors } from '../../app/layout/theme'
import { Account } from '../../types'
import { useState } from 'react'

interface AccountListProps {
  accounts: Account[]
  isLoading: boolean
  onNewClick: () => void
  onSearch: (query: string) => void
}

export const AccountList = ({ accounts, isLoading, onNewClick, onSearch }: AccountListProps) => {
  const [searchQuery, setSearchQuery] = useState('')

  const columns = [
    { id: 'name', label: 'Account Name' },
    { id: 'industry', label: 'Industry' },
    { id: 'phone', label: 'Phone' },
    { id: 'website', label: 'Website' },
    { id: 'owner', label: 'Account Owner' },
  ]

  if (isLoading) return <LoadingSpinner fullHeight label="Loading accounts..." />

  return (
    <Box>
      <Box
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: themeColors.surface,
          borderBottom: `1px solid ${themeColors.border}`,
        }}
      >
        <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>
          {accounts.length} item{accounts.length !== 1 ? 's' : ''} · Updated a few seconds ago
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ImportIcon sx={{ fontSize: 15 }} />}
            sx={{
              fontSize: '13px',
              borderColor: themeColors.border,
              color: themeColors.text.primary,
              '&:hover': { borderColor: themeColors.primary, color: themeColors.primary, backgroundColor: themeColors.primaryLight },
            }}
          >
            Import
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={onNewClick}
            sx={{
              fontSize: '13px',
              background: themeColors.primary, '&:hover': { background: themeColors.primaryDark },
              boxShadow: '0 2px 6px rgba(0,112,210,0.3)',
            }}
          >
            New
          </Button>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: themeColors.border }} />
          <Tooltip title="Refresh">
            <IconButton size="small" sx={{ color: themeColors.text.secondary }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filters">
            <IconButton size="small" sx={{ color: themeColors.text.secondary }}>
              <FilterIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <SearchBar
            value={searchQuery}
            onSearch={(v) => { setSearchQuery(v); onSearch(v) }}
            placeholder="Search accounts..."
          />
          <DataTable
            columns={columns}
            rows={accounts}
            emptyMessage="No accounts found"
            selectable
            showRowNumbers
          />
        </Stack>
      </Box>
    </Box>
  )
}
